<script lang="ts">
  import type { BankMatch, BankAccount, SettlementDetails } from "./PcpSettlementTask/types";
  import SettlementStatus, { type TaskStep } from "./PcpSettlementTask/elements/SettlementStatus.svelte";
  import RailsForm from "../components/Form.svelte";
  import Workflow from "../lib/workflow/Workflow.svelte";
  import Step from "../lib/workflow/Step.svelte";
  import Aside from "../lib/workflow/Aside.svelte";
  import Header from "./Settlement/elements/Header.svelte";
  import LoadingOverlay from "./Settlement/elements/LoadingOverlay.svelte";
  import Overview from "./PcpSettlementTask/steps/Overview.svelte";
  import Recommendation from "./PcpSettlementTask/steps/Recommendation.svelte";
  import ConfirmFees from "./PcpSettlementTask/steps/ConfirmFees.svelte";
  import OfferObjected from "./PcpSettlementTask/steps/OfferObjected.svelte";
  import BankChoice from "./Settlement/steps/BankChoice.svelte";
  import BankForm from "./Settlement/steps/BankForm.svelte";
  import BankMatchResult from "./Settlement/steps/BankMatchResult.svelte";
  import PaymentSteps from "./PcpSettlementTask/steps/PaymentSteps.svelte";
  import PaymentSent from "./PcpSettlementTask/steps/PaymentSent.svelte";
  import CannotAddBank from "./Settlement/steps/CannotAddBank.svelte";
  import IdRequired from "./Settlement/steps/IdRequired.svelte";

  let {
    action,
    errors,
    currentStep,
    clientName,
    opposingPartyName,
    settlementDetails,
    jlgMainPhone,
    nextSettlementTaskUrl,
    blockAddingBank,
    bankMatch,
    existingBankAccounts,
    idRequiredUrl,
  }: {
    action: string;
    errors: { [key: string]: string } | null;
    currentStep: TaskStep;
    clientName: string;
    opposingPartyName: string;
    settlementDetails: SettlementDetails;
    jlgMainPhone: string;
    nextSettlementTaskUrl: string | null;
    blockAddingBank: boolean;
    bankMatch: BankMatch;
    existingBankAccounts: BankAccount[];
    idRequiredUrl: string | null;
  } = $props();

  let minStep = $derived(
    {
      response: 1,
      confirm: 1,
      add_bank: 3,
      receive_pay: 4,
      payment_sent: 5,
      waiting_for_updated_offer: 5,
    }[currentStep] || 0,
  );

  let objected = $derived(currentStep == "waiting_for_updated_offer");
  let anotherAccountExists = $derived(existingBankAccounts.length !== 0);

  // svelte-ignore state_referenced_locally
  let bankAccountChoice = $state<string | undefined>(existingBankAccounts[0]?.id);

  // svelte-ignore state_referenced_locally
  const reopened = minStep >= 4; // Intentionally based on initial, not updated, props

  const scrollToTop = () => document.querySelector("#task-modal .modal-box")?.scrollTo(0, 0);
</script>

<RailsForm {action} method="put">
  <Workflow state={{ step: 0, totalSteps: 4, caption: "Overview" }} onchange={scrollToTop}>
    {#if reopened}
      <div class="-mx-4 -mt-4 mb-4 rounded-t-lg overflow-clip">
        <SettlementStatus {currentStep} payoutAmount={settlementDetails.payoutAmount} objection={settlementDetails.objection} />
      </div>
    {:else}
      <Header {minStep} amount={settlementDetails.payoutAmount} badge={objected && "Countered"}/>
    {/if}
    <div class="ui-rhythm">
      {#if errors?.general}<div class="ui-error">{errors.general}</div>{/if}
      <Step state={{caption: "Preflight"}} disabled={!idRequiredUrl}>
        <IdRequired url={idRequiredUrl!} phone={jlgMainPhone} />
      </Step>
      <Step disabled={minStep >= 4}>
        <Overview {opposingPartyName} {settlementDetails} {jlgMainPhone} offerAccepted={minStep >= 3} />
      </Step>
      <Step state={{step: 1}} disabled={minStep >= 3}><Recommendation {settlementDetails} {jlgMainPhone} {errors} /></Step>
      <Step state={{step: 2}} disabled={minStep >= 3}><ConfirmFees {settlementDetails} /></Step>
      <Step state={{step: 3}} disabled={minStep >= 4 || !anotherAccountExists} path="bank-choice">
        <BankChoice bind:selectedOption={bankAccountChoice} {existingBankAccounts} {errors} />
      </Step>
      <Step state={{step: 3}} disabled={minStep >= 4 || !blockAddingBank}>
        <CannotAddBank phone={jlgMainPhone} back={anotherAccountExists} />
      </Step>
      <Step state={{step: 3}} disabled={minStep >= 4}>
        <BankForm {clientName} {jlgMainPhone} {errors} allowBack={true} />
      </Step>
      <Step state={{step: 3}} disabled={minStep >= 4}>
        <BankMatchResult {bankMatch} phone={jlgMainPhone} backToBankChoice={anotherAccountExists} />
      </Step>
      <Step state={{step: 4}} disabled={minStep >= 5}>
        <PaymentSteps {settlementDetails} {bankMatch} {jlgMainPhone} {reopened} {nextSettlementTaskUrl} />
      </Step>
      <Step disabled={minStep != 5 || objected}>
        <PaymentSent {settlementDetails} {bankMatch} {jlgMainPhone} {nextSettlementTaskUrl} />
      </Step>
      <Step state={{step: 2, totalSteps: 2}} disabled={minStep != 5 || !objected}>
        <OfferObjected {opposingPartyName} {settlementDetails} {jlgMainPhone} {reopened} {nextSettlementTaskUrl} />
      </Step>
    </div>

    <Aside name="name-matching">
      <LoadingOverlay
        title="Verifying bank account"
        description="For your security, we’re ensuring your bank account matches our records. This may take a few moments." />
    </Aside>
  </Workflow>
</RailsForm>
